import { Trans, useLingui } from '@lingui/react/macro'
import {
  getCurrentSubscription,
  isPushSupported,
  requestNotificationPermission,
  subscribeToPush,
  unsubscribeFromPush,
} from '$src/lib/pushNotifications'
import {
  useGetVapidPublicKey,
  useSubscribe,
  useUnsubscribe,
} from '$src/lib/trpcHooks/pushSubscription'
import { Box, Card, Group, Stack, Switch, Text } from '@mantine/core'
import { assertNotNil } from '@markku/utils'
import { IconBell } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

export function NotificationSettings() {
  const { t } = useLingui()
  const [isEnabling, setIsEnabling] = useState(false)
  const { data: vapidKey } = useGetVapidPublicKey()
  const { mutateAsync: subscribe } = useSubscribe()
  const { mutateAsync: unsubscribe } = useUnsubscribe()

  // Query for current browser subscription state
  const { data: browserSubscription, refetch: refetchBrowserSubscription } =
    useQuery({
      queryKey: ['browserPushSubscription'],
      queryFn: getCurrentSubscription,
    })

  const isSubscribed = browserSubscription !== null
  const isSupported = isPushSupported()

  // Don't render if push is not supported
  if (!isSupported) {
    return null
  }

  const isPermissionDenied = Notification.permission === 'denied'

  const handleToggle = async () => {
    if (isSubscribed) {
      // Unsubscribe
      try {
        setIsEnabling(true)
        const success = await unsubscribeFromPush()
        if (success && browserSubscription) {
          await unsubscribe({ endpoint: browserSubscription.endpoint })
        }
        await refetchBrowserSubscription()
      } catch (error) {
        console.error('Failed to unsubscribe:', error)
      } finally {
        setIsEnabling(false)
      }
    } else {
      // Subscribe
      try {
        setIsEnabling(true)

        // Request permission if not granted
        if (Notification.permission !== 'granted') {
          const permission = await requestNotificationPermission()
          if (permission !== 'granted') {
            return
          }
        }

        // Get VAPID key and subscribe
        if (!vapidKey?.publicKey) {
          throw new Error('VAPID public key not available')
        }

        const subscription = await subscribeToPush(vapidKey.publicKey)

        // Send subscription to backend
        await subscribe({
          endpoint: subscription.endpoint,
          p256dhKey: assertNotNil(subscription.toJSON().keys?.p256dh),
          authKey: assertNotNil(subscription.toJSON().keys?.auth),
        })

        await refetchBrowserSubscription()
      } catch (error) {
        console.error('Failed to subscribe:', error)
      } finally {
        setIsEnabling(false)
      }
    }
  }

  return (
    <Card radius="md" withBorder>
      <Group justify="space-between" align="center" wrap="nowrap">
        <Group gap="md" wrap="nowrap">
          <Box
            style={{
              width: 40,
              height: 40,
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--mantine-color-blue-0)',
            }}
          >
            <IconBell size={20} color="var(--mantine-color-blue-6)" />
          </Box>
          <Stack gap={0}>
            <Text fw={600}>
              <Trans>Push notifications</Trans>
            </Text>
            <Text c="dimmed" size="sm">
              {isPermissionDenied
                ? t`Permission denied in browser`
                : isSubscribed
                  ? t`You receive notifications on this device`
                  : t`Receive notifications`}
            </Text>
          </Stack>
        </Group>
        <Switch
          checked={isSubscribed}
          onChange={handleToggle}
          disabled={isEnabling || isPermissionDenied}
          size="md"
        />
      </Group>
    </Card>
  )
}
